import React, { useMemo } from 'react';
import type { ChartComponentProps } from './chartConfig';
import { formatValue, formatActual } from '../../utils/numberFormat';

/**
 * Matrix visual aligned to repo-2: financial-table cells with soft
 * semantic tinting and a per-column magnitude bar.
 */

const TINT: Record<string, string> = {
  primary: 'rgba(44, 95, 124, 0.10)',
  positive: 'rgba(94, 139, 94, 0.18)',
  negative: 'rgba(166, 61, 64, 0.18)',
  accent: 'rgba(232, 184, 79, 0.20)',
  neutral: 'rgba(138, 129, 120, 0.08)',
  secondary: 'rgba(111, 176, 209, 0.14)',
};

const TONE_COLOR: Record<string, string> = {
  primary: '#2C5F7C',
  secondary: '#6FB0D1',
  positive: '#5E8B5E',
  negative: '#A63D40',
  accent: '#E8B84F',
  neutral: '#8A8178',
};

function normalizeTone(tone: string | null | undefined): string | undefined {
  const normalized = String(tone || '').toLowerCase().trim();
  return normalized || undefined;
}

// When the LLM omits ``tone`` on a cell (or stamps a generic
// ``primary``/``neutral``), derive tinting from the value sign so the
// matrix grid still reads as coloured at a glance — positive cells
// green, negative cells red. Returns ``undefined`` for null/missing
// values so empty cells stay transparent.
function deriveToneFromValue(value: number | null | undefined): string | undefined {
  if (typeof value !== 'number' || !Number.isFinite(value)) return undefined;
  if (value > 0) return 'positive';
  if (value < 0) return 'negative';
  return 'neutral';
}

function formatMatrixValue(value: number | null | undefined, format?: string) {
  if (value == null || !Number.isFinite(Number(value))) return '--';
  return formatValue(Number(value), { unit: format });
}

function sameDisplayLabel(left: string | undefined | null, right: string | undefined | null): boolean {
  const l = String(left || '').replace(/\s+/g, ' ').trim().toLowerCase();
  const r = String(right || '').replace(/\s+/g, ' ').trim().toLowerCase();
  return Boolean(l && r && l === r);
}

export const MatrixChart: React.FC<ChartComponentProps> = ({ chart, height = 320, presentation }) => {
  const cells = useMemo(() => chart.cells || [], [chart.cells]);
  const rows = useMemo(() => Array.from(new Set(cells.map((cell) => cell.row))), [cells]);
  const cols = useMemo(() => Array.from(new Set(cells.map((cell) => cell.col))), [cells]);
  const cellMap = useMemo(() => {
    const map = new Map<string, NonNullable<typeof cells>[number]>();
    cells.forEach((cell) => map.set(`${cell.row}\u200B${cell.col}`, cell));
    return map;
  }, [cells]);

  const colMax = useMemo(() => {
    const maxByColumn = new Map<string, number>();
    cols.forEach((col) => {
      const max = rows.reduce((currentMax, row) => {
        const value = cellMap.get(`${row}\u200B${col}`)?.value;
        return typeof value === 'number' && Number.isFinite(value)
          ? Math.max(currentMax, Math.abs(value))
          : currentMax;
      }, 0);
      maxByColumn.set(col, max || 1);
    });
    return maxByColumn;
  }, [cellMap, cols, rows]);

  const valueRange = useMemo(() => {
    let min = Infinity;
    let max = -Infinity;
    cells.forEach((cell) => {
      const v = cell?.value;
      if (typeof v === 'number' && Number.isFinite(v)) {
        if (v < min) min = v;
        if (v > max) max = v;
      }
    });
    return Number.isFinite(min) && Number.isFinite(max) && min !== max
      ? { min, max }
      : null;
  }, [cells]);

  if (cells.length === 0 || rows.length === 0 || cols.length === 0) {
    return (
      <div className="flex items-center justify-center rounded-[10px] border border-[#EDE6E1] bg-[#FAF7F1] text-sm text-[#7B6B72]" style={{ height }}>
        No matrix cells supplied.
      </div>
    );
  }

  const valueMeasure = chart.matrix_value_measure || '';
  const secondaryMeasure = chart.matrix_secondary_measure || '';
  const valueFormat = String(
    (valueMeasure && (
      chart.metric_formats?.[valueMeasure]
      || chart.metric_unit_symbols?.[valueMeasure]
      || chart.metric_units?.[valueMeasure]
    ))
    || chart.axes?.y?.format
    || chart.y_axis_label
    || ''
  );
  const secondaryFormat = String(
    (secondaryMeasure && (
      chart.metric_formats?.[secondaryMeasure]
      || chart.metric_unit_symbols?.[secondaryMeasure]
      || chart.metric_units?.[secondaryMeasure]
    ))
    || ''
  );
  const valueLabel = (valueMeasure && chart.metric_display_names?.[valueMeasure]) || 'Value';
  const secondaryLabel = (secondaryMeasure && chart.metric_display_names?.[secondaryMeasure]) || 'Secondary';
  const showSecondary = Boolean(secondaryMeasure) && !sameDisplayLabel(valueLabel, secondaryLabel);
  const rowHeaderLabel = chart.metric_display_names?.Name || chart.metric_display_names?.name || '';
  const useExternalScroll = presentation?.useExternalScroll === true;

  return (
    <div className="flex flex-col gap-3" style={{ maxHeight: useExternalScroll ? undefined : height }}>
      <div
        className={useExternalScroll ? 'overflow-visible' : 'overflow-auto'}
        style={{ maxHeight: useExternalScroll ? undefined : height }}
      >
        <div
          className="grid min-w-[520px] border-l border-t border-[#EDE6E1] bg-white text-[12px]"
          style={{ gridTemplateColumns: `minmax(150px, max-content) repeat(${cols.length}, minmax(120px, 1fr))` }}
        >
          <div className="border-b border-r border-[#EDE6E1] bg-[#FAF7F1] px-3 py-2 text-[11px] uppercase tracking-[0.12em] text-[#7B6B72]">
            {rowHeaderLabel}
          </div>
          {cols.map((col) => (
            <div key={col} className="border-b border-r border-[#EDE6E1] bg-[#FAF7F1] px-3 py-2 text-right text-[11px] uppercase tracking-[0.12em] text-[#7B6B72]">
              {col}
            </div>
          ))}

          {rows.map((row) => (
            <React.Fragment key={row}>
              <div className="truncate border-b border-r border-[#EDE6E1] px-3 py-2 font-mono text-[11.5px] text-[#3F3338]" title={row}>
                {row}
              </div>
              {cols.map((col) => {
                const cell = cellMap.get(`${row}\u200B${col}`);
                const value = cell?.value ?? null;
                const secondaryValue = cell?.secondary_value ?? null;
                const hasValue = typeof value === 'number' && Number.isFinite(value);
                const hasSecondaryValue = typeof secondaryValue === 'number' && Number.isFinite(secondaryValue);
                const explicitTone = normalizeTone(cell?.tone);
                const tone = (
                  explicitTone && explicitTone !== 'primary' && explicitTone !== 'neutral'
                    ? explicitTone
                    : deriveToneFromValue(value)
                ) || explicitTone;
                const bgCss = tone ? TINT[tone] || TINT.primary : 'transparent';
                const barColor = TONE_COLOR[tone || 'primary'] || TONE_COLOR.primary;
                const columnMax = colMax.get(col) || 1;
                const barWidthPct = hasValue ? Math.min(100, Math.round((Math.abs(value) / columnMax) * 100)) : 0;
                return (
                  <div
                    key={`${row}-${col}`}
                    className="relative border-b border-r border-[#EDE6E1] px-3 py-2 text-right"
                    style={{ background: bgCss }}
                    title={[
                      `${row} · ${col}: ${valueLabel} ${value == null || !Number.isFinite(Number(value)) ? '--' : formatActual(Number(value), { unit: valueFormat })}`,
                      showSecondary && hasSecondaryValue ? `${secondaryLabel} ${formatActual(Number(secondaryValue), { unit: secondaryFormat })}` : '',
                    ].filter(Boolean).join(' | ')}
                  >
                    <div className="relative z-[1] flex flex-col items-end gap-0.5">
                      <span className="font-mono text-[12.5px] tabular-nums text-[#1F1A20]">
                        {formatMatrixValue(value, valueFormat)}
                      </span>
                      {showSecondary && hasSecondaryValue && (
                        <span className="font-mono text-[10.5px] tabular-nums text-[#7B6B72]">
                          {formatMatrixValue(secondaryValue, secondaryFormat)}
                        </span>
                      )}
                    </div>
                    <div
                      className="absolute bottom-0 left-0 h-[2px]"
                      style={{
                        width: `${barWidthPct}%`,
                        background: barColor,
                        opacity: hasValue ? 0.7 : 0,
                      }}
                    />
                  </div>
                );
              })}
            </React.Fragment>
          ))}
        </div>
      </div>
      {valueRange && (
        <div className="flex items-center gap-3 px-1 pt-1 text-[10.5px] uppercase tracking-[0.08em] text-[#7B6B72]">
          <span className="font-mono tabular-nums normal-case text-[#3F3338]">
            {formatMatrixValue(valueRange.min, valueFormat)}
          </span>
          <span>Low</span>
          <div
            className="h-2 flex-1 rounded-full"
            style={{
              background: `linear-gradient(to right, ${TONE_COLOR.negative}, ${TONE_COLOR.neutral}, ${TONE_COLOR.positive})`,
            }}
          />
          <span>High</span>
          <span className="font-mono tabular-nums normal-case text-[#3F3338]">
            {formatMatrixValue(valueRange.max, valueFormat)}
          </span>
        </div>
      )}
    </div>
  );
};
